import { QueryClient, useInfiniteQuery } from 'react-query';
import { QueryKeys } from './QueryKeys';
import { apiFetch, ApiFetch, useApiClient } from './useApiClient';

const PAGE_SIZE = 12;

type Params = {
  readonly username?: string;
};

type ProfileRecipe = {
  readonly id: string;
  readonly name: string;
  readonly image_url?: string;
};

export type ProfileRecipesByUsernameQueryResult = {
  readonly recipes: readonly ProfileRecipe[];
};

export function useProfileRecipesByUsernameQuery({ username, ...params }: Params) {
  const api = useApiClient();

  return useInfiniteQuery(
    QueryKeys.profileRecipesByUsername(username, params),
    ({ pageParam = 0 }) => fetchProfileRecipesByUsername(api.fetch, pageParam, { username, ...params }),
    {
      enabled: !!username,
      getNextPageParam: (lastPage, pages) => (lastPage.recipes.length >= PAGE_SIZE ? pages.length : undefined),
    },
  );
}

export async function prefetchProfileRecipesByUsernameQuery(client: QueryClient, { username, ...params }: Params) {
  return await client.prefetchInfiniteQuery(QueryKeys.profileRecipesByUsername(username, params), () =>
    fetchProfileRecipesByUsername(apiFetch, 0, { username, ...params }),
  );
}

async function fetchProfileRecipesByUsername(
  fetch: ApiFetch,
  page: number,
  { username }: Params,
): Promise<ProfileRecipesByUsernameQueryResult> {
  const res = await fetch(`/web/profiles/${username}/recipes`, {
    query: {
      offset: page * PAGE_SIZE,
      limit: PAGE_SIZE,
    },
  });

  return res.json();
}
